import React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { EASE_CELEBRATE } from '../../motion';
import { SAVED_CTA_FILL, SAVED_CTA_INK } from './savedCtaTokens';

// Connected "drawer" Save bar — attaches flush to the bottom of a product
// card's photo (which goes flat-bottomed via ResultsProductCard's `imageFooter`
// slot) with a flat top + rounded bottom corners, reading as one unit with the
// image. It sits BELOW the photo, never over it, so the product is never
// obscured (the prior overlay pill covered the lower ~15% of every image).
//
// Color affordance: solid brand-clay fill when unsaved so the bar reads as a
// clear CTA against the cream feed (cream-on-cream was invisible — Kate
// 2026-06-04). Saved calms to the liked-rose pair so a long feed isn't a wall
// of terracotta.
//
// The saved look is the PDP's "Saved" CTA, token for token — the SAME
// `hsl(var(--liked))` text on `hsl(var(--liked) / 0.14)` fill, and no outline
// (board ask, THE-690: "match the pink saved option"). It used to be a clay
// tint (#FBEFEA) under clay text + a clay rule, which read terracotta next to
// the sheet's pink and made the two doors to the same state look like two
// different states. ProductDetailSheet's `LikeCta` is the other half of this
// pair; change them together or the sheet and the card drift apart again.
//
// Extracted from BoardFeed (THE-684) so the gift guide can wear the SAME bar
// rather than a lookalike — the ask was explicitly "like what exists on the
// recipient board cards today", and two copies of this CSS would drift on the
// next tweak. Presentational only: the two surfaces disagree about what Save
// MEANS and each keeps its own semantics in its own file —
//
//   board — one-way. Save flies the card to the saved tray and the bar settles
//     inert ($saved + disabled); un-save lives on the saved-tray X.
//   gift guide — add-only, never inert. Save opens the "Who's this for?"
//     picker, and a saved card stays tappable so the same gift can be saved
//     for a SECOND person (the guide has no recipient context of its own).
//
// so `disabled` is the caller's call, not this component's.
const Drawer = styled.button<{ $saved: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 44px;
  /* Explicit rather than inherited from bootstrap's reboot: Storybook (and so
     every Happo snapshot) does not load bootstrap.css, and under content-box
     this bar renders 2px WIDER than the photo it hangs off — a drift between
     what we review and what ships. No-op in the app. */
  box-sizing: border-box;
  /* Declared on all four sides in BOTH states so the content box — and so the
     label's baseline — can't shift when the bar flips to saved. Only the COLOR
     changes: clay-on-clay (invisible) when unsaved, transparent when saved.
     The saved state carries no outline at all, matching the PDP CTA; the
     earlier fix for this bug closed a three-sided clay rule across the top
     (THE-690 round 1), and the board's follow-up was to drop the rule rather
     than close it. Flat TOP CORNERS (radius below) are what make the bar read
     as one unit with the photo — not the border. */
  border: 1px solid ${({ $saved, theme }) => ($saved ? 'transparent' : theme.color.clay)};
  border-radius: 0 0 16px 16px;
  background: ${({ $saved, theme }) => ($saved ? SAVED_CTA_FILL : theme.color.clay)};
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  color: ${({ $saved }) => ($saved ? SAVED_CTA_INK : '#FFFFFF')};
  transition: background 150ms ease, color 150ms ease;
  &:active { transform: scale(0.99); }
  &:disabled { cursor: default; }
`;

/** THE-1264's first-visit reveal: one ping on the first gift's heart, in
 *  lockstep with the header card's own spotlight (`BoardHeaderCard`'s
 *  `reveal.spotlight`) — decorative, not a real save, so `saved`/
 *  `heartFilled` are untouched by it. `EASE_CELEBRATE` on purpose: this IS
 *  the house's save/like curve, and a heart ping is exactly that moment. */
const heartPing = keyframes`
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.4); }
`;

const HeartGlyphSvg = styled.svg<{ $pulse?: boolean }>`
  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${heartPing} 500ms ${EASE_CELEBRATE} 1;
    `}
`;

const HeartGlyph: React.FC<{ filled: boolean; pulse?: boolean }> = ({ filled, pulse }) => (
  <HeartGlyphSvg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill={filled ? SAVED_CTA_INK : 'none'}
    /* Unsaved heart sits on the solid clay bar → white outline; saved heart
       sits on the rose tint → liked-rose, filled, matching the label beside
       it and the PDP's saved CTA. */
    stroke={filled ? SAVED_CTA_INK : '#FFFFFF'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    $pulse={pulse}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </HeartGlyphSvg>
);

export interface CardSaveDrawerProps {
  /** Durable saved state — drives the label ("Save" / "Saved") and the calm
   *  saved tint. Also the accessible name, which is what the card's own
   *  top-right heart is hidden BY on both surfaces: they hide
   *  `button[aria-label='Save'/'Unsave']:not([data-board-save])`, and this
   *  bar's label is never "Unsave", so the selector can't reach it. */
  saved: boolean;
  /** Fill the heart glyph. The board passes its OPTIMISTIC predicate so the
   *  heart fills on click instead of waiting for the Firestore listener;
   *  omit and it follows `saved`. */
  heartFilled?: boolean;
  /** Inert bar (board: mid-flight or already saved). Defaults to false —
   *  the guide's bar is always live. */
  disabled?: boolean;
  /** THE-1264 — one decorative ping on this card's heart, during the phone
   *  first-visit reveal. Omitted by every caller but the board feed's first
   *  card, which only passes it once per browser. */
  pulse?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const CardSaveDrawer: React.FC<CardSaveDrawerProps> = ({
  saved,
  heartFilled,
  disabled = false,
  pulse,
  onClick,
}) => (
  <Drawer
    type="button"
    aria-label={saved ? 'Saved' : 'Save'}
    /* Historical name (it was board-only); it means "this card's own save
       drawer" and is the hook BOTH surfaces' heart-hiding CSS excludes, plus
       the handle board tests grab the bar by. Renaming it would have to land
       in three places at once for no gain. */
    data-board-save
    $saved={saved}
    disabled={disabled}
    onClick={onClick}
  >
    <HeartGlyph filled={heartFilled ?? saved} pulse={pulse} />
    {saved ? 'Saved' : 'Save'}
  </Drawer>
);

export default CardSaveDrawer;
